import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    CircularProgress,
    Box,
    InputAdornment,
    Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { useSelector } from 'react-redux';

export default function ProcessFolioRefundModal({ open, onClose, folioId, folioBalance = 0, onSuccess }) {
    const { currency, currencySymbol } = useCurrency();
    const { user } = useSelector((state) => state.auth || {});
    const activeTenantId = user?.tenantId;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [form, setForm] = useState({
        amount: '',
        refundMethod: '',
        reason: '',
        date: '',
        sendEmail: true
    });

    const fetchPaymentMethods = async () => {
        setLoadingMethods(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const resolvedTenantId = activeTenantId || authData.tenantId || null;
            const res = await SettingsService.getPaymentMethods(resolvedTenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            setPaymentMethods(activeList);
        } catch (err) {
            console.error('Failed to load refund methods from API:', err);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    useEffect(() => {
        if (open) {
            const now = new Date();
            const localISOTime = (new Date(now.getTime() - now.getTimezoneOffset() * 60000)).toISOString().slice(0,10);
            
            setForm({
                amount: folioBalance < 0 ? Math.abs(folioBalance).toFixed(2) : '',
                refundMethod: '',
                reason: '',
                date: localISOTime,
                sendEmail: true
            });

            fetchPaymentMethods();
        }
    }, [open, folioBalance]);

    const handleTextChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleCheckboxChange = (event) => {
        setForm(prev => ({ ...prev, sendEmail: event.target.checked }));
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (!val || val.match(/^\d*\.?\d*$/)) {
            setForm(prev => ({ ...prev, amount: val }));
        }
    };

    const handleSubmit = async () => {
        if (!form.amount || !form.refundMethod) {
            toast.error('Please fill out all required refund fields.');
            return;
        }

        const amountVal = parseFloat(form.amount);
        if (isNaN(amountVal) || amountVal <= 0) {
            toast.error('Refund amount must be a positive number greater than zero.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                amount: amountVal,
                paymentMethod: form.refundMethod,
                type: 'Refund',
                reason: form.reason.trim() || 'Process Refund via UI',
                date: new Date(form.date).toISOString(),
                sendEmail: form.sendEmail
            };

            await FinanceService.processFolioRefund(folioId, payload);
            toast.success('Refund successfully processed; guest folio balance updated');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to process folio refund:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to process folio refund';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={!isSubmitting ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' } }}>
            <DialogTitle sx={{ color: '#1e293b', display: 'flex', alignItems: 'center', fontWeight: 'bold', pb: 1 }}>
                <UndoIcon sx={{ mr: 1, color: '#3b82f6', transform: 'rotate(180deg)' }} /> Process Refund
            </DialogTitle>
            <DialogContent sx={{ p: 4, pt: 2 }}>
                <Box sx={{ mt: 1 }}>
                    <FormControl fullWidth required sx={{ mb: 3 }}>
                        <InputLabel>Refund Method</InputLabel>
                        <Select
                            value={form.refundMethod}
                            label="Refund Method"
                            onChange={handleTextChange('refundMethod')}
                            disabled={isSubmitting || loadingMethods}
                        >
                            {loadingMethods ? (
                                <MenuItem disabled value="">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">Loading refund methods...</Typography>
                                    </Box>
                                </MenuItem>
                            ) : paymentMethods.length > 0 ? (
                                paymentMethods.map((method, idx) => {
                                    const val = typeof method === 'string' ? method : (method.name || method.code || method.id);
                                    const label = typeof method === 'string' ? method : (method.label || method.name || method.code || val);
                                    return (
                                        <MenuItem key={method.id || method._id || val || idx} value={val}>
                                            {label}
                                        </MenuItem>
                                    );
                                })
                            ) : (
                                <MenuItem disabled value="">No refund methods available</MenuItem>
                            )}
                            {form.refundMethod && !paymentMethods.some(m => (typeof m === 'string' ? m : (m.name || m.code || m.id)) === form.refundMethod) && (
                                <MenuItem key={form.refundMethod} value={form.refundMethod}>
                                    {form.refundMethod}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Refund Amount"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={handleAmountChange}
                        disabled={isSubmitting}
                        helperText={`Maximum allowable refund: ${formatCurrency(folioBalance, currency)}`}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
                <Button 
                    onClick={onClose} 
                    disabled={isSubmitting}
                    variant="outlined"
                    sx={{ 
                        color: '#4f46e5', borderColor: '#4f46e5', fontWeight: 600, px: 3, borderRadius: 6,
                        textTransform: 'none'
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ 
                        px: 3, borderRadius: 6, fontWeight: 600, color: 'white',
                        bgcolor: '#4f46e5', textTransform: 'none',
                        '&:hover': { bgcolor: '#4338ca' }
                    }}
                >
                    {isSubmitting ? 'Processing...' : 'Issue Refund'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
